// QuickBooks, read into Base Camp (PRD rev. 1.22, QB1–QB8). Pure rules, shared by the API and the
// browser: what the sync stores, how QuickBooks's P&L detail report becomes lines, Base Camp's own
// spending categories and how QuickBooks accounts map to them, customers to clients, and the queue
// of money that still needs a client or a category.
//
// QuickBooks stays the book of record. Base Camp reads it (QB3) and keeps its own mappings beside
// it; nothing here is ever written back.

import { addMonths, type Iso, type Month } from "./dates";
import { clientIdOf } from "./clients";

export type BooksBasis = "cash" | "accrual";
export const BOOKS_BASES: readonly BooksBasis[] = ["cash", "accrual"];

/** History starts here (Chris, 2026-10-28), so a year can be compared with the last. */
export const BOOKS_HISTORY_FROM: Month = "2025-01";

export interface BooksAccount {
  id: string;
  name: string;
  /** The full name QuickBooks shows, "Contractors:Brett Swank" for a sub-account. */
  fullName: string;
  parentId?: string;
  /** QuickBooks's classification: Revenue, Expense, Asset, Liability, Equity. */
  classification?: string;
  type?: string;
  active: boolean;
}

export interface BooksParty {
  id: string;
  name: string;
  active: boolean;
}

/** One line of QuickBooks's P&L detail: a transaction's amount in one account. */
export interface BooksLine {
  /** Stable across syncs: the transaction, its account and its place in the report. */
  key: string;
  date: Iso;
  kind: "income" | "expense";
  txnType: string;
  txnId?: string;
  docNumber?: string;
  accountId: string;
  accountName: string;
  /** The customer, vendor or employee QuickBooks names on the line. */
  partyId?: string;
  partyName?: string;
  memo?: string;
  amount: number;
  /** All money (FR9): the bank, card or Undeposited Funds account the money moved through. */
  via?: string;
  /** All money: a move between 3Seven's own money accounts, which nets to nothing. */
  transfer?: boolean;
}

// --- Categories (QB5) ---------------------------------------------------------------------------------

/** How People's subcategories drill in (QB6): to each partner, employee, contractor or benefit. */
export type PeopleGroup = "partners" | "employees" | "contractors" | "benefits";

export interface SpendCategory {
  id: string;
  name: string;
  /** A subcategory names its parent; two levels only. */
  parentId?: string;
  people?: PeopleGroup;
  /** The shared-cost category its company spending feeds (FR10.7); a subcategory takes its parent's. */
  shared?: string;
}

export const UNMAPPED = "unmapped";

/** Base Camp's spending categories, seeded from 3Seven's 2026 accounts (rev. 1.22, §4.18). */
export const DEFAULT_SPEND_CATEGORIES: readonly SpendCategory[] = [
  { id: "people", name: "People" },
  { id: "people-partners", name: "Partners", parentId: "people", people: "partners" },
  { id: "people-employees", name: "Employees", parentId: "people", people: "employees" },
  { id: "people-contractors", name: "Contractors", parentId: "people", people: "contractors" },
  { id: "people-benefits", name: "Benefits", parentId: "people", people: "benefits" },
  { id: "events", name: "Events and go-to-market" },
  { id: "events-advertising", name: "Advertising", parentId: "events" },
  { id: "events-meals", name: "Meals", parentId: "events" },
  { id: "travel", name: "Travel" },
  { id: "professional", name: "Professional services" },
  { id: "professional-accounting", name: "Accounting", parentId: "professional" },
  { id: "professional-legal", name: "Legal", parentId: "professional" },
  { id: "office", name: "Office, software and admin" },
  { id: "insurance", name: "Insurance" },
  { id: "taxes", name: "Taxes and licenses" },
  { id: "financing", name: "Financing" },
  // Money that isn't income or spending on the P&L (FR9): it moves through the bank all the same.
  { id: "owners", name: "Owners' draws and contributions" },
  { id: "loans", name: "Loans" },
  { id: "balance", name: "Other balance sheet" },
  { id: UNMAPPED, name: "Unmapped" },
];

/** Categories every list needs, added to a saved list that predates them (FR9). */
export const REQUIRED_CATEGORIES: readonly SpendCategory[] = DEFAULT_SPEND_CATEGORIES.filter((c) => ["owners", "loans", "balance", UNMAPPED].includes(c.id));
export const withRequiredCategories = (cats: readonly SpendCategory[]): SpendCategory[] => [
  ...cats.filter((c) => c.id !== UNMAPPED),
  ...REQUIRED_CATEGORIES.filter((r) => r.id !== UNMAPPED && !cats.some((c) => c.id === r.id)),
  cats.find((c) => c.id === UNMAPPED) ?? REQUIRED_CATEGORIES.find((r) => r.id === UNMAPPED)!,
];

/**
 * The first sync's suggestions, by account name, in order: the first rule that matches wins. Benefits
 * come before Insurance so medical insurance is a people cost; "401(K) Fees" before payroll.
 */
const SUGGESTIONS: [RegExp, string][] = [
  [/guaranteed payment/i, "people-partners"],
  [/medical|dental|vision|work ?comp|401\s*\(?k\)?\s*fees|human interest/i, "people-benefits"],
  [/payroll|wages|salar|401\s*\(?k\)?\s*payable/i, "people-employees"],
  [/contractor|1099|subcontract/i, "people-contractors"],
  [/advertis|marketing/i, "events-advertising"],
  [/meals|entertainment/i, "events-meals"],
  [/event|summit|go to market|conference|offsite/i, "events"],
  [/travel|airfare|lodging|mileage/i, "travel"],
  [/accounting|bookkeep/i, "professional-accounting"],
  [/attorney|legal/i, "professional-legal"],
  [/office|software|subscription|bank charge|bank fee|postage|telephone|internet/i, "office"],
  [/insurance|erisa|bond/i, "insurance"],
  [/tax|licen/i, "taxes"],
  [/interest/i, "financing"],
];

/** A suggested category for an account from its name, or Unmapped. */
export function suggestCategory(accountName: string): string {
  return SUGGESTIONS.find(([re]) => re.test(accountName))?.[1] ?? UNMAPPED;
}

export interface BooksMapping {
  /** QuickBooks account id → Base Camp category id. */
  accounts: Record<string, string>;
  /** QuickBooks customer id → Base Camp client name, where the names differ (QB4). */
  customers: Record<string, string>;
  /** Set once the first sync has applied the suggestions; afterwards a new account is Unmapped (QB5). */
  seeded: boolean;
  /** Set once balance-sheet accounts have had their suggestions too (FR9). */
  seededBalance?: boolean;
}

/** Where money sits (FR9): bank and credit-card accounts, and Undeposited Funds, where payments wait for the bank. */
export const isMoneyAccount = (a: Pick<BooksAccount, "type" | "name">): boolean => a.type === "Bank" || a.type === "Credit Card" || /undeposited funds/i.test(a.name);
const isReceivablePayable = (a: Pick<BooksAccount, "type">) => a.type === "Accounts Receivable" || a.type === "Accounts Payable";
const isBalanceSheet = (a: BooksAccount) => (a.classification === "Asset" || a.classification === "Liability" || a.classification === "Equity") && !isMoneyAccount(a) && !isReceivablePayable(a);

/** A balance-sheet account's suggested category, by its type and name (FR9). */
export function suggestBalanceCategory(a: Pick<BooksAccount, "name" | "type" | "classification">): string {
  if (a.classification === "Equity" || /draw|distribution|contribution|owner|partner.*capital|capital/i.test(a.name)) return "owners";
  if (/payroll|401\s*\(?k\)?|withholding|benefit/i.test(a.name)) return "people-employees";
  if (/sales tax|tax payable|income tax/i.test(a.name)) return "taxes";
  if (a.type === "Long Term Liability" || /loan|line of credit|note payable|ppp|sba|eidl|mortgage/i.test(a.name)) return "loans";
  return "balance";
}

/** Balance-sheet accounts get their suggestions once, the first sync after all-money arrives (FR9). */
export function seedBalanceSheet(mapping: BooksMapping, accounts: readonly BooksAccount[]): BooksMapping {
  if (mapping.seededBalance) return mapping;
  const next = { ...mapping.accounts };
  for (const a of accounts) if (!a.parentId && next[a.id] === undefined && isBalanceSheet(a)) next[a.id] = suggestBalanceCategory(a);
  return { ...mapping, accounts: next, seededBalance: true };
}

export const emptyMapping = (): BooksMapping => ({ accounts: {}, customers: {}, seeded: false });

/** The first sync maps every expense account by its suggestion; later syncs leave new ones Unmapped. */
export function seedMapping(mapping: BooksMapping, accounts: readonly BooksAccount[]): BooksMapping {
  if (mapping.seeded) return mapping;
  const next = { ...mapping.accounts };
  for (const a of accounts) if (!a.parentId && next[a.id] === undefined && isSpendAccount(a)) next[a.id] = suggestCategory(a.name);
  return { ...mapping, accounts: next, seeded: true };
}

const isSpendAccount = (a: BooksAccount) => !a.classification || a.classification === "Expense";

/** An account's category: its own mapping, else its parent's (a sub-account inherits), else Unmapped. */
export function categoryOfAccount(accountId: string, accounts: ReadonlyMap<string, BooksAccount>, mapping: BooksMapping): string {
  for (let id: string | undefined = accountId, depth = 0; id && depth < 6; id = accounts.get(id)?.parentId, depth++) {
    const c = mapping.accounts[id];
    if (c) return c;
  }
  return UNMAPPED;
}

export function validateCategories(categories: readonly SpendCategory[]): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  for (const c of categories) {
    if (typeof c?.id !== "string" || !/^[a-z0-9-]{1,40}$/.test(c.id)) errors.push("Each category needs a short id of letters, digits and dashes.");
    else if (ids.has(c.id)) errors.push("Each category needs its own id.");
    else ids.add(c.id);
    if (typeof c?.name !== "string" || !c.name.trim() || c.name.length > 60) errors.push("Each category needs a name of up to 60 characters.");
  }
  for (const c of categories) {
    if (!c.parentId) continue;
    const parent = categories.find((p) => p.id === c.parentId);
    if (!parent) errors.push(`${c.name}'s parent category is missing.`);
    else if (parent.parentId) errors.push("Categories go two levels deep, no further.");
  }
  if (!ids.has(UNMAPPED)) errors.push("The Unmapped category can't be removed: it's where new accounts land.");
  return [...new Set(errors)];
}

/** The top-level category a category belongs to. */
export const topCategory = (id: string, categories: readonly SpendCategory[]): string => categories.find((c) => c.id === id)?.parentId ?? id;

// --- Customers to clients (QB4) -------------------------------------------------------------------------

/**
 * The Base Camp client a QuickBooks customer is: the admin's override, else a known client with the
 * same name (ignoring case and spacing, as projects find theirs), else the customer's own name.
 */
export function clientOfCustomer(customer: BooksParty, known: readonly string[], mapping: BooksMapping): { client: string; matched: boolean; override: boolean } {
  const override = mapping.customers[customer.id];
  if (override) return { client: override, matched: true, override: true };
  const id = clientIdOf(customer.name);
  const hit = known.find((k) => clientIdOf(k) === id);
  return hit ? { client: hit, matched: true, override: false } : { client: customer.name, matched: false, override: false };
}

// --- Assigning what QuickBooks leaves open (QB8) -----------------------------------------------------------

export interface LineAssignment {
  clientName?: string;
  categoryId?: string;
  by: string;
  at: string;
}

export const NOT_ASSIGNED = "Not yet assigned";

/** What a line counts as after mappings and assignments: its client (income) or category (spending). */
export interface ResolvedLine extends BooksLine {
  clientName?: string;
  categoryId?: string;
  /** Decided by an admin in Base Camp rather than read from QuickBooks or a mapping. */
  assigned: boolean;
  /** Needs a client or a category: shown in the Assign queue. */
  open: boolean;
}

export interface BooksContext {
  accounts: ReadonlyMap<string, BooksAccount>;
  customers: ReadonlyMap<string, BooksParty>;
  knownClients: readonly string[];
  mapping: BooksMapping;
  assignments: Readonly<Record<string, LineAssignment>>;
}

/**
 * A line's client or category. Income takes its customer's client; with no customer, the admin's
 * assignment, else Not yet assigned. Spending takes the admin's assignment for the line, else its
 * account's category. QuickBooks's own answer wins over an assignment once it has one (QB8): a
 * customer set later in QuickBooks retires the line's assignment.
 */
export function resolveLine(line: BooksLine, ctx: BooksContext): ResolvedLine {
  const a = ctx.assignments[line.key];
  if (line.transfer) return { ...line, assigned: false, open: false };
  if (line.kind === "income") {
    const customer = line.partyId ? ctx.customers.get(line.partyId) : undefined;
    if (customer) return { ...line, clientName: clientOfCustomer(customer, ctx.knownClients, ctx.mapping).client, assigned: false, open: false };
    if (a?.clientName) return { ...line, clientName: a.clientName, assigned: true, open: false };
    // Money in from no customer (an owner's contribution, a loan) counts by its account's category (FR9).
    if (line.via) {
      const categoryId = categoryOfAccount(line.accountId, ctx.accounts, ctx.mapping);
      if (categoryId !== UNMAPPED) return { ...line, categoryId, assigned: false, open: false };
    }
    return { ...line, clientName: NOT_ASSIGNED, assigned: false, open: true };
  }
  if (a?.categoryId) return { ...line, categoryId: a.categoryId, assigned: true, open: false };
  const categoryId = categoryOfAccount(line.accountId, ctx.accounts, ctx.mapping);
  return { ...line, categoryId, assigned: false, open: categoryId === UNMAPPED };
}

/** Assignments for lines QuickBooks now answers itself: they can be dropped. */
export function retiredAssignments(lines: readonly BooksLine[], ctx: BooksContext): string[] {
  const byKey = new Map(lines.map((l) => [l.key, l]));
  return Object.keys(ctx.assignments).filter((k) => {
    const l = byKey.get(k);
    return !!l && l.kind === "income" && !!l.partyId && ctx.customers.has(l.partyId);
  });
}

// --- QuickBooks's P&L detail report -------------------------------------------------------------------------

type ColData = { value?: string; id?: string };
interface ReportRow {
  type?: string;
  group?: string;
  Header?: { ColData?: ColData[] };
  Rows?: { Row?: ReportRow[] };
  ColData?: ColData[];
  Summary?: unknown;
}
interface Report {
  Columns?: { Column?: { ColTitle?: string; ColType?: string; MetaData?: { Name: string; Value: string }[] }[] };
  Rows?: { Row?: ReportRow[] };
}

const INCOME_GROUPS = new Set(["Income", "OtherIncome"]);
const EXPENSE_GROUPS = new Set(["Expenses", "OtherExpenses", "COGS", "CostOfGoodsSold"]);
/** Section headers that name a group, not an account. */
const GROUP_HEADERS = /^(ordinary income\/expenses?|income|other income|expenses?|other expenses?|cost of goods sold|gross profit|net (operating |other )?income)$/i;

type Column = NonNullable<NonNullable<Report["Columns"]>["Column"]>[number];
const colKeys = (c: Column) => [c.MetaData?.find((m) => m.Name === "ColKey")?.Value, c.ColType, c.ColTitle].filter((x): x is string => !!x).map((x) => x.toLowerCase());

/**
 * Where each field sits, found by the column's key, type or title (QuickBooks names the same column
 * differently by report version and by the columns asked for), so a column in another place, or
 * under another of its names, still reads.
 */
function columnsOf(r: Report) {
  const cols = (r.Columns?.Column ?? []).map(colKeys);
  const find = (...names: string[]) => cols.findIndex((ks) => names.some((n) => ks.includes(n.toLowerCase())));
  let amount = find("subt_nat_amount", "nat_amount", "subt_nat_home_amount", "nat_home_amount", "amount");
  // Never the running balance; failing a known name, the last money column that isn't one.
  if (amount < 0) for (let i = cols.length - 1; i >= 0 && amount < 0; i--) if (cols[i].includes("money") && !cols[i].some((k) => k.includes("rbal") || k.includes("balance"))) amount = i;
  return {
    date: find("tx_date", "date", "transaction date"),
    type: find("txn_type", "transaction type"),
    doc: find("doc_num", "num", "no."),
    name: find("name", "cust_name", "vend_name"),
    memo: find("memo", "memo/description", "description"),
    split: find("split_acc", "split"),
    amount,
    count: cols.length,
  };
}

const kindOfClass = (c: string | undefined): BooksLine["kind"] | undefined =>
  !c ? undefined : /revenue|income/i.test(c) ? "income" : /expense|cost/i.test(c) ? "expense" : undefined;

/**
 * QuickBooks's ProfitAndLossDetail report (one basis, one range) as lines. Each data row sits inside
 * its account's section, inside an income or expense group; columns are found by their keys, types or
 * titles. Where QuickBooks gives no group, the account's classification, then the section's title,
 * says income or spending; where a section names no account id, its name is matched to the chart of
 * accounts, and failing that kept as its own account. Amounts are as QuickBooks shows them: income
 * and spending positive, a refund or deduction negative.
 */
export function parseProfitAndLossDetail(report: unknown, accountsByName?: ReadonlyMap<string, BooksAccount>): BooksLine[] {
  const r = report as Report;
  const c = columnsOf(r);
  const out: BooksLine[] = [];
  const counts = new Map<string, number>();
  const cell = (cd: ColData[], i: number) => (i >= 0 ? cd[i] : undefined);
  const byId = new Map([...(accountsByName?.values() ?? [])].map((a) => [a.id, a]));
  const walk = (rows: ReportRow[] | undefined, kind: BooksLine["kind"] | undefined, account: { id: string; name: string } | undefined) => {
    for (const row of rows ?? []) {
      const group = row.group;
      let k = group && INCOME_GROUPS.has(group) ? "income" : group && EXPENSE_GROUPS.has(group) ? "expense" : kind;
      if (row.Rows?.Row || row.Header) {
        const head = row.Header?.ColData?.[0];
        const name = head?.value?.trim() ?? "";
        // A group's own title decides when QuickBooks gives no group: never a heading that spans both.
        if (!group && /^(other )?income$/i.test(name)) k = "income";
        else if (!group && /^((other )?expenses?|cost of goods sold)$/i.test(name)) k = "expense";
        let acct = account;
        if (name && !GROUP_HEADERS.test(name)) {
          const known = (head?.id ? byId.get(head.id) : undefined) ?? accountsByName?.get(name.toLowerCase());
          acct = known ? { id: known.id, name: known.name } : { id: head?.id || `name:${name}`, name };
          k = kindOfClass(known?.classification) ?? k;
        }
        walk(row.Rows?.Row, k, acct);
        continue;
      }
      const cd = row.ColData;
      if (!cd || !k || !account || row.type === "Section") continue;
      const amount = Number((cell(cd, c.amount)?.value ?? "").replace(/[,$\s]/g, ""));
      const date = (cell(cd, c.date)?.value ?? "").slice(0, 10);
      if (!Number.isFinite(amount) || amount === 0 || !/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
      const typeCell = cell(cd, c.type);
      const txnId = typeCell?.id || undefined;
      const doc = cell(cd, c.doc)?.value;
      const party = cell(cd, c.name);
      const memo = cell(cd, c.memo)?.value;
      const base = `${txnId ?? `${date}-${doc ?? ""}`}:${account.id}`;
      const n = (counts.get(base) ?? 0) + 1;
      counts.set(base, n);
      out.push({
        key: n === 1 ? base : `${base}:${n}`,
        date,
        kind: k,
        txnType: typeCell?.value ?? "",
        ...(txnId ? { txnId } : {}),
        ...(doc ? { docNumber: doc } : {}),
        accountId: account.id,
        accountName: account.name,
        ...(party?.id ? { partyId: party.id } : {}),
        ...(party?.value ? { partyName: party.value } : {}),
        ...(memo ? { memo } : {}),
        amount,
      });
    }
  };
  walk(r.Rows?.Row, undefined, undefined);
  return out;
}

/**
 * A report's layout, never its contents: column names, how many sections and rows, the groups, and
 * what kind of value fills each cell of the first data row (date, number, text or empty). Kept with
 * a sync that reads no lines, so a layout the reader misses can be fixed without seeing a figure.
 */
export function reportShape(report: unknown): { columns: string[]; sections: number; dataRows: number; groups: string[]; firstRow: string[]; found: Record<string, number> } {
  const r = report as Report;
  const c = columnsOf(r);
  const columns = (r.Columns?.Column ?? []).map((x) => colKeys(x).join("|"));
  let sections = 0;
  let dataRows = 0;
  const groups = new Set<string>();
  let firstRow: string[] = [];
  const kind = (v: string | undefined) => (!v ? "empty" : /^\d{4}-\d{2}-\d{2}/.test(v) ? "date" : /^-?[\d,]+(\.\d+)?$/.test(v.replace(/[$\s]/g, "")) ? "number" : "text");
  const walk = (rows: ReportRow[] | undefined) => {
    for (const row of rows ?? []) {
      if (row.group) groups.add(row.group);
      if (row.Rows?.Row || row.Header) {
        sections++;
        walk(row.Rows?.Row);
      } else if (row.ColData) {
        dataRows++;
        if (!firstRow.length) firstRow = row.ColData.map((x) => `${kind(x.value)}${x.id ? "+id" : ""}`);
      }
    }
  };
  walk(r.Rows?.Row);
  return { columns, sections, dataRows, groups: [...groups], firstRow, found: { date: c.date, amount: c.amount, type: c.type, name: c.name } };
}

/** The months a sync reads: from `from` to `to`, inclusive. */
export function monthRange(from: Month, to: Month): Month[] {
  const out: Month[] = [];
  for (let m = from; m <= to && out.length < 240; m = addMonths(m, 1)) out.push(m);
  return out;
}

/** What the nightly run re-reads: this month and the two before, since entries still land there (QB2). */
export const nightlyMonths = (thisMonth: Month): Month[] => monthRange(addMonths(thisMonth, -2), thisMonth);

/**
 * QuickBooks's own totals in a P&L detail report: each income and expense group's summary line, as
 * QuickBooks adds them up. The reports tie Base Camp's sums to these (FR8).
 */
export function reportTotals(report: unknown): { income: number; expense: number } {
  const r = report as Report;
  const iAmount = columnsOf(r).amount;
  const out = { income: 0, expense: 0 };
  const walk = (rows: ReportRow[] | undefined) => {
    for (const row of rows ?? []) {
      const g = row.group;
      const kind = g && INCOME_GROUPS.has(g) ? "income" : g && EXPENSE_GROUPS.has(g) ? "expense" : undefined;
      if (kind) {
        const summary = (row.Summary as { ColData?: ColData[] } | undefined)?.ColData;
        const v = Number((iAmount >= 0 ? (summary?.[iAmount]?.value ?? "") : "").replace(/[,$\s]/g, ""));
        if (Number.isFinite(v)) out[kind] += v;
        continue; // a group's own summary already counts everything inside it
      }
      walk(row.Rows?.Row);
    }
  };
  walk(r.Rows?.Row);
  return out;
}


// --- All money: the General Ledger of the money accounts (FR9) ---------------------------------------------

/** The split column's name when a transaction touches several accounts. */
export const SEVERAL_ACCOUNTS = "several";

/**
 * QuickBooks's GeneralLedger report, read for the money accounts only (FR9): every deposit, payment,
 * charge and transfer, with the account on the other side ("Split"). Each line's `accountId` is that
 * other side, so it takes the other side's category as a P&L line does; `via` is the money account.
 * A bank or Undeposited Funds line is money in when positive; a credit card's is money out when
 * positive (a charge raises what's owed). A line whose other side is another money account is a
 * transfer: shown apart, never as income or spending.
 */
export function parseGeneralLedger(report: unknown, accounts: readonly BooksAccount[]): BooksLine[] {
  const r = report as Report;
  const c = columnsOf(r);
  const byName = new Map(accounts.flatMap((a) => [[a.name.toLowerCase(), a] as const, [a.fullName.toLowerCase(), a] as const]));
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const out: BooksLine[] = [];
  const counts = new Map<string, number>();
  const cell = (cd: ColData[], i: number) => (i >= 0 ? cd[i] : undefined);
  const walk = (rows: ReportRow[] | undefined, money: BooksAccount | undefined) => {
    for (const row of rows ?? []) {
      if (row.Rows?.Row || row.Header) {
        const head = row.Header?.ColData?.[0];
        const name = head?.value?.trim() ?? "";
        const acct = (head?.id ? byId.get(head.id) : undefined) ?? byName.get(name.toLowerCase());
        walk(row.Rows?.Row, acct ? (isMoneyAccount(acct) ? acct : undefined) : money);
        continue;
      }
      const cd = row.ColData;
      if (!cd || !money || row.type === "Section") continue;
      const raw = Number((cell(cd, c.amount)?.value ?? "").replace(/[,$\s]/g, ""));
      const date = (cell(cd, c.date)?.value ?? "").slice(0, 10);
      if (!Number.isFinite(raw) || raw === 0 || !/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
      const card = money.type === "Credit Card";
      const moneyIn = card ? raw < 0 : raw > 0;
      const splitCell = cell(cd, c.split);
      const splitName = (splitCell?.value ?? "").trim();
      const several = !splitName || /^-?\s*split\s*-?$/i.test(splitName);
      const other = several ? undefined : ((splitCell?.id ? byId.get(splitCell.id) : undefined) ?? byName.get(splitName.toLowerCase()));
      const typeCell = cell(cd, c.type);
      const txnId = typeCell?.id || undefined;
      const doc = cell(cd, c.doc)?.value;
      const party = cell(cd, c.name);
      const memo = cell(cd, c.memo)?.value;
      const base = `money:${txnId ?? `${date}-${doc ?? ""}`}:${money.id}`;
      const n = (counts.get(base) ?? 0) + 1;
      counts.set(base, n);
      out.push({
        key: n === 1 ? base : `${base}:${n}`,
        date,
        kind: moneyIn ? "income" : "expense",
        txnType: typeCell?.value ?? "",
        ...(txnId ? { txnId } : {}),
        ...(doc ? { docNumber: doc } : {}),
        accountId: several ? SEVERAL_ACCOUNTS : (other?.id ?? `name:${splitName}`),
        accountName: several ? "Several accounts" : (other?.name ?? splitName),
        ...(party?.id ? { partyId: party.id } : {}),
        ...(party?.value ? { partyName: party.value } : {}),
        ...(memo ? { memo } : {}),
        amount: Math.abs(raw),
        via: money.name,
        ...(other && isMoneyAccount(other) ? { transfer: true } : {}),
      });
    }
  };
  walk(r.Rows?.Row, undefined);
  return settleCombinedTransfers(out);
}

/**
 * A deposit of several payments from Undeposited Funds shows in the bank's ledger as one line against
 * "-Split-", while Undeposited Funds shows each payment leaving for the bank. The bank line is the
 * same money moving, so it's a transfer too: whole, when the other side covers it, or less what the
 * other side covers, when the deposit also carries other funds (FR9). Otherwise it would count as
 * income a second time, beside the payments already counted by customer.
 */
function settleCombinedTransfers(lines: BooksLine[]): BooksLine[] {
  const moved = new Map<string, { via: string; amount: number }[]>();
  for (const l of lines) if (l.transfer && l.txnId) moved.set(l.txnId, [...(moved.get(l.txnId) ?? []), { via: l.via ?? "", amount: l.amount }]);
  const out: BooksLine[] = [];
  for (const l of lines) {
    if (l.transfer || l.accountId !== SEVERAL_ACCOUNTS || !l.txnId || !moved.has(l.txnId)) {
      out.push(l);
      continue;
    }
    const covered = moved.get(l.txnId)!.filter((m) => m.via !== l.via).reduce((t, m) => t + m.amount, 0);
    if (covered <= 0.005) out.push(l);
    else if (covered >= l.amount - 0.005) out.push({ ...l, transfer: true });
    else out.push({ ...l, transfer: true, amount: covered, key: `${l.key}:moved` }, { ...l, amount: l.amount - covered });
  }
  return out;
}
